import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, ArrowRight, Shield } from 'lucide-react';

const HUD_BG = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/kt7pneq2_image_3a5e170c-65ec-4af5-a940-10dfd1e1d196.png';
const SHIELD_LOGO = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/znp5fjmw_image_7fc4ee3c-94ac-47f2-acda-a583cc0a2f45.png';
const QUADRA_LOGO = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/xofpw4gy_image_0852ba71-f307-4acc-b4f4-a151e3175634.png';

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('subscriber');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, username, role);
      }
      navigate('/scan');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" data-testid="auth-page">
      {/* HUD Background */}
      <div className="absolute inset-0">
        <img src={HUD_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[rgba(13,13,13,0.65)]" />
      </div>

      <div className="relative z-10 w-full max-w-md px-4">
        {/* Logo + Branding */}
        <div className="text-center mb-8 animate-fade-in-up">
          <div className="flex items-center justify-center gap-3 mb-3">
            <img src={SHIELD_LOGO} alt="SMITHERS" className="w-10 h-10 object-contain" />
          </div>
          <h1 className="text-3xl font-black" style={{ fontFamily: 'Orbitron' }}>
            <span className="neon-cyan">SMITHERS</span> <span className="neon-magenta">XI</span>
          </h1>
          <p className="text-sm text-[--ch-text-secondary] mt-2">
            {isLogin ? 'Welcome back, Creator' : 'Stake your claim'}
          </p>
        </div>

        {/* Auth Card - glass effect over HUD */}
        <div className="rounded-2xl p-8 animate-fade-in-up stagger-1 border border-[--ch-border]"
          style={{ background: 'rgba(13,13,13,0.85)', backdropFilter: 'blur(20px)' }}
          data-testid="auth-form-card">

          <div className="flex mb-6 rounded-lg overflow-hidden border border-[--ch-border]">
            <button
              data-testid="auth-login-tab"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2.5 text-sm font-semibold transition-all ${isLogin ? 'bg-[--ch-cyan] text-black' : 'text-[--ch-text-secondary] hover:text-white'}`}
            >
              Sign In
            </button>
            <button
              data-testid="auth-register-tab"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2.5 text-sm font-semibold transition-all ${!isLogin ? 'bg-[--ch-magenta] text-black' : 'text-[--ch-text-secondary] hover:text-white'}`}
            >
              Sign Up
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <label className="text-sm text-[--ch-text-secondary] mb-1 block">Username</label>
                  <input
                    data-testid="auth-username-input"
                    type="text" value={username} onChange={e => setUsername(e.target.value)}
                    className="w-full bg-[rgba(0,0,0,0.4)] border border-[--ch-border] rounded-lg px-4 py-2.5 text-white focus:border-[--ch-cyan] focus:outline-none transition-colors"
                    placeholder="Choose a username" required
                  />
                </div>
                <div>
                  <label className="text-sm text-[--ch-text-secondary] mb-1 block">Account Type</label>
                  <div className="flex gap-2">
                    <button type="button" data-testid="auth-role-subscriber" onClick={() => setRole('subscriber')}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${role === 'subscriber' ? 'border-[--ch-cyan] text-[--ch-cyan] bg-[rgba(0,240,255,0.1)]' : 'border-[--ch-border] text-[--ch-text-secondary]'}`}>
                      Subscriber
                    </button>
                    <button type="button" data-testid="auth-role-creator" onClick={() => setRole('creator')}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${role === 'creator' ? 'border-[--ch-magenta] text-[--ch-magenta] bg-[rgba(255,0,229,0.1)]' : 'border-[--ch-border] text-[--ch-text-secondary]'}`}>
                      Creator
                    </button>
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="text-sm text-[--ch-text-secondary] mb-1 block">Email</label>
              <input
                data-testid="auth-email-input"
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                className="w-full bg-[rgba(0,0,0,0.4)] border border-[--ch-border] rounded-lg px-4 py-2.5 text-white focus:border-[--ch-cyan] focus:outline-none transition-colors"
                placeholder="your@email.com" required
              />
            </div>

            <div className="relative">
              <label className="text-sm text-[--ch-text-secondary] mb-1 block">Password</label>
              <input
                data-testid="auth-password-input"
                type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                className="w-full bg-[rgba(0,0,0,0.4)] border border-[--ch-border] rounded-lg px-4 py-2.5 pr-10 text-white focus:border-[--ch-cyan] focus:outline-none transition-colors"
                placeholder="Enter password" required
              />
              <button type="button" data-testid="auth-toggle-password" onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-8 text-[--ch-text-secondary]">
                {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {error && (
              <div data-testid="auth-error" className="text-[--ch-error] text-sm bg-[rgba(239,68,68,0.1)] border border-[--ch-error] rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button data-testid="auth-submit-btn" type="submit" disabled={loading}
              className="w-full btn-neon py-3 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold disabled:opacity-50">
              {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
              <ArrowRight size={16} />
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[--ch-border]" />
            <span className="text-sm text-[--ch-text-secondary]">or</span>
            <div className="flex-1 h-px bg-[--ch-border]" />
          </div>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button data-testid="google-signin-btn"
            onClick={() => {
              const redirectUrl = window.location.origin + '/scan';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-lg border border-[--ch-border] bg-[rgba(0,0,0,0.3)] hover:bg-[rgba(0,240,255,0.05)] hover:border-[rgba(0,240,255,0.3)] transition-all text-sm font-semibold">
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          {isLogin && (
            <div className="mt-4 text-center">
              <p className="text-sm text-[--ch-text-secondary]">
                Demo: test@smithers.com / test123
              </p>
            </div>
          )}
        </div>

        {/* Quadra Core badge */}
        <div className="flex items-center justify-center gap-3 mt-6 animate-fade-in-up stagger-2">
          <img src={QUADRA_LOGO} alt="Quadra Core" className="h-4 object-contain opacity-40" />
          <span className="text-xs text-[--ch-text-secondary]">Secured by QUADRACore OS</span>
        </div>
      </div>
    </div>
  );
}
